"use client";

import { IconShare } from "@decibeltrade/react-icons";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";
import { css } from "styled-system/css";
import { flex, stack } from "styled-system/patterns";

import { Card } from "@/components/card/card";
import { MediaQuery } from "@/components/media-query";

import { GradientText } from "./gradient-text";
import { openShareCardModal } from "./share-card-modal";

type TierLevel = "gold" | "doublePlatinum" | "diamond";

interface TierInfo {
  name: string;
  image: string;
  pointsRequired: string;
  progress: number;
}

interface TierCardProps {
  currentTier: TierLevel;
  totalPoints: string;
  rank: string;
  tiers: Record<TierLevel, TierInfo>;
}

const tierImageStyles = css({
  w: "full",
  h: "auto",
  objectFit: "cover",
});

const progressOverlayStyles = css({
  position: "absolute",
  textStyle: { base: "heading.3xs", md: "heading.2xs" },
  color: "text.primary",
});

const tierVideoSources: Record<TierLevel, string> = {
  gold: "/images/points/tier-gold-ring.webm",
  doublePlatinum: "/images/points/tier-platinum-ring.webm",
  diamond: "/images/points/tier-diamond-ring.webm",
};

interface AnimatedTierRingProps {
  tier: TierLevel;
  progress: number;
  size: number;
  className?: string;
}

function AnimatedTierRing({ tier, progress, size, className }: AnimatedTierRingProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const rafRef = useRef<number>(0);
  const [ready, setReady] = useState(false);
  const isCompleted = progress >= 100;

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    setReady(false);

    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    }

    const showOnSeeked = () => {
      setReady(true);
      video.removeEventListener("seeked", showOnSeeked);
    };

    const handleVideo = () => {
      if (!video.duration || !isFinite(video.duration) || isNaN(video.duration)) return;

      video.pause();

      if (isCompleted) {
        video.addEventListener("seeked", showOnSeeked);
        video.currentTime = video.duration;
      } else if (progress <= 0) {
        video.currentTime = 0;
        setReady(true);
      } else {
        const targetVideoTime = (progress / 100) * video.duration;
        const animDurationMs = 800;
        let startTs: number | null = null;

        // Animate from empty (time=0) to target
        setReady(true);

        const animate = (timestamp: number) => {
          if (startTs === null) startTs = timestamp;
          const elapsed = timestamp - startTs;
          const t = Math.min(elapsed / animDurationMs, 1);
          const eased = 1 - Math.pow(1 - t, 3);
          video.currentTime = targetVideoTime * eased;
          if (t < 1) {
            rafRef.current = requestAnimationFrame(animate);
          }
        };

        rafRef.current = requestAnimationFrame(animate);
      }
    };

    if (video.readyState >= 1) {
      handleVideo();
    } else {
      video.addEventListener("loadedmetadata", handleVideo);
    }

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = 0;
      }
      video.removeEventListener("loadedmetadata", handleVideo);
      video.removeEventListener("seeked", showOnSeeked);
      video.pause();
    };
  }, [progress, isCompleted]);

  return (
    <video
      ref={videoRef}
      src={tierVideoSources[tier]}
      width={size}
      height={size}
      muted
      playsInline
      preload="auto"
      className={className}
      style={{
        objectFit: "cover",
        background: "transparent",
        mixBlendMode: "screen",
        transform: "scale(1.08)",
        opacity: ready ? 1 : 0,
      }}
    />
  );
}

export function TierCard({ currentTier, totalPoints, rank, tiers }: TierCardProps) {
  const t = useTranslations("points.tier");

  const handleShare = () => {
    openShareCardModal({
      points: totalPoints,
      rank,
      tier: currentTier,
      tierName: tiers[currentTier].name,
    });
  };

  return (
    <Card
      background="gradient"
      className={stack({
        p: "0",
        w: "full",
        minH: { base: "auto", lg: "[620px]" },
        rounded: "350",
        position: "relative",
        overflow: "hidden",
        justifyContent: "space-between",
      })}
    >
      <MediaQuery display="below" breakpoint="md">
        {(className) => (
          <div className={className}>
            <MobileTierLayout
              currentTier={currentTier}
              totalPoints={totalPoints}
              tiers={tiers}
              t={t}
            />
          </div>
        )}
      </MediaQuery>

      <MediaQuery display="above" breakpoint="md">
        {(className) => (
          <div className={className}>
            <DesktopTierLayout
              currentTier={currentTier}
              totalPoints={totalPoints}
              tiers={tiers}
              t={t}
            />
          </div>
        )}
      </MediaQuery>

      <button
        className={css({
          w: "full",
          bg: "background.tertiary",
          py: "12",
          px: "8",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8",
          border: "none",
          cursor: "pointer",
          textStyle: "heading.3xs",
          color: "text.primary",
          textTransform: "uppercase",
          _hover: { bg: "brandYellow.550", color: "black" },
          transitionPreset: "colors",
        })}
        onClick={handleShare}
      >
        <IconShare className={css({ w: "16", h: "16" })} />
        <span>{t("share")}</span>
      </button>
    </Card>
  );
}

interface TierLayoutProps {
  currentTier: TierLevel;
  totalPoints: string;
  tiers: Record<TierLevel, TierInfo>;
  t: ReturnType<typeof useTranslations<"points.tier">>;
}

function MobileTierLayout({ currentTier, tiers, t }: TierLayoutProps) {
  return (
    <>
      <div
        className={stack({
          gap: "0",
          px: { base: "12", sm: "16" },
          pt: { base: "16", sm: "24" },
        })}
      >
        <h2
          className={css({
            textStyle: { base: "heading.lg", sm: "heading.xl" },
            color: "text.primary",
            textAlign: "left",
          })}
        >
          {t("yourTier")} <GradientText variant="gold">{tiers[currentTier].name}</GradientText>
        </h2>
      </div>

      <div
        className={stack({ gap: "0", pl: { base: "8", sm: "16" }, pt: { base: "24", sm: "40" } })}
      >
        <MobileTierRow
          tier="gold"
          label={t("gold")}
          pointsRequired={tiers.gold.pointsRequired}
          progress={tiers.gold.progress}
          imageSrc={tiers.gold.image}
          t={t}
        />

        <MobileTierRow
          tier="doublePlatinum"
          label={t("doublePlatinum")}
          pointsRequired={tiers.doublePlatinum.pointsRequired}
          progress={tiers.doublePlatinum.progress}
          imageSrc={tiers.doublePlatinum.image}
          t={t}
          connectorWidth={{ base: "[90px]", sm: "[137px]" } as const}
          mt={{ base: "24", sm: "48" } as const}
        />

        <MobileTierRow
          tier="diamond"
          label={t("diamond")}
          pointsRequired={tiers.diamond.pointsRequired}
          progress={tiers.diamond.progress}
          imageSrc={tiers.diamond.image}
          t={t}
          connectorWidth={{ base: "[90px]", sm: "[137px]" } as const}
          mt={{ base: "24", sm: "48" } as const}
        />
      </div>

      <div className={css({ h: { base: "24", sm: "48" } })} />
    </>
  );
}

interface MobileTierRowProps {
  tier: TierLevel;
  label: string;
  pointsRequired: string;
  progress: number;
  imageSrc: string;
  t: ReturnType<typeof useTranslations<"points.tier">>;
  connectorWidth?: { readonly base: string; readonly sm: string };
  mt?: { readonly base: string; readonly sm: string };
}

function MobileTierRow({
  tier,
  label,
  pointsRequired,
  progress,
  imageSrc,
  t,
  connectorWidth = { base: "[80px]", sm: "[115px]" } as const,
  mt,
}: MobileTierRowProps) {
  return (
    <div
      className={flex({
        alignItems: "flex-start",
        ...(mt && { mt: mt as { base: "24"; sm: "48" } }),
      })}
    >
      <div
        className={css({
          position: "relative",
          w: { base: "[140px]", sm: "[180px]" },
          h: { base: "[140px]", sm: "[180px]" },
          flexShrink: "0",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        })}
      >
        {progress >= 100 ? (
          <Image src={imageSrc} alt={label} width={180} height={180} className={tierImageStyles} />
        ) : (
          <>
            <AnimatedTierRing
              tier={tier}
              progress={progress}
              size={180}
              className={tierImageStyles}
            />
            {progress > 0 && <span className={progressOverlayStyles}>{progress}%</span>}
          </>
        )}
      </div>
      <div
        className={stack({
          flex: "1",
          alignItems: "center",
          justifyContent: "center",
          pt: { base: "16", sm: "32" },
        })}
      >
        <span
          className={css({
            textStyle: { base: "subheading.sm", sm: "subheading.md" },
            color: "text.primary",
            textAlign: "center",
          })}
        >
          {label}
        </span>
        <div
          className={css({
            w: connectorWidth as { base: "[80px]" | "[90px]"; sm: "[115px]" | "[137px]" },
            h: { base: "[14px]", sm: "[19px]" },
            mt: { base: "8", sm: "16" },
            mb: { base: "8", sm: "12" },
          })}
        >
          <Image
            src="/images/points/tier-connector-line.svg"
            alt=""
            width={137}
            height={19}
            className={css({ transform: "scaleY(-1) rotate(180deg)", w: "full", h: "full" })}
          />
        </div>
        <div className={stack({ gap: "0", alignItems: "center" })}>
          <div className={flex({ gap: { base: "2", sm: "4" }, alignItems: "baseline" })}>
            <span
              className={css({
                textStyle: { base: "heading.xs", sm: "heading.sm" },
                color: "text.primary",
              })}
            >
              {pointsRequired}
            </span>
            <span
              className={css({
                textStyle: { base: "heading.xs", sm: "heading.sm" },
                color: "text.tertiary",
              })}
            >
              {t("pts")}
            </span>
          </div>
          <span
            className={css({
              textStyle: "heading.3xs",
              color: "text.tertiary",
              textTransform: "uppercase",
            })}
          >
            {t("required")}
          </span>
        </div>
      </div>
    </div>
  );
}

function DesktopTierLayout({ currentTier, tiers, t }: TierLayoutProps) {
  return (
    <>
      <div
        className={flex({
          justify: "flex-start",
          alignItems: "flex-start",
          w: "full",
          px: "24",
          pt: "24",
        })}
      >
        <h2 className={css({ textStyle: "heading.lg", color: "text.primary" })}>
          {t("yourTier")} <GradientText variant="gold">{tiers[currentTier].name}</GradientText>
        </h2>
      </div>

      <div
        className={flex({
          justify: "center",
          alignItems: "center",
          w: "full",
          pt: "72",
          pb: "8",
          px: "24",
        })}
      >
        <DesktopTierItem
          tier="gold"
          label={t("gold")}
          progress={tiers.gold.progress}
          imageSrc={tiers.gold.image}
        />

        <TierConnectorLine />

        <DesktopTierItem
          tier="doublePlatinum"
          label={t("doublePlatinum")}
          progress={tiers.doublePlatinum.progress}
          imageSrc={tiers.doublePlatinum.image}
        />

        <TierConnectorLine />

        <DesktopTierItem
          tier="diamond"
          label={t("diamond")}
          progress={tiers.diamond.progress}
          imageSrc={tiers.diamond.image}
        />
      </div>

      <div
        className={flex({
          justify: "space-around",
          alignItems: "center",
          w: "full",
          px: "24",
          pt: "16",
          pb: "16",
        })}
      >
        <TierRequirement pointsRequired={tiers.gold.pointsRequired} t={t} />
        <TierRequirement pointsRequired={tiers.doublePlatinum.pointsRequired} t={t} />
        <TierRequirement pointsRequired={tiers.diamond.pointsRequired} t={t} />
      </div>
    </>
  );
}

interface DesktopTierItemProps {
  tier: TierLevel;
  label: string;
  progress: number;
  imageSrc: string;
}

function DesktopTierItem({ tier, label, progress, imageSrc }: DesktopTierItemProps) {
  return (
    <div className={stack({ alignItems: "center", gap: "16" })}>
      <div
        className={css({
          position: "relative",
          w: "[215px]",
          h: "[215px]",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        })}
      >
        {progress >= 100 ? (
          <Image src={imageSrc} alt={label} width={215} height={215} className={tierImageStyles} />
        ) : (
          <>
            <AnimatedTierRing
              tier={tier}
              progress={progress}
              size={215}
              className={tierImageStyles}
            />
            {progress > 0 && <span className={progressOverlayStyles}>{progress}%</span>}
          </>
        )}
      </div>
      <span
        className={css({
          textStyle: "subheading.md",
          color: "text.primary",
          textAlign: "center",
          w: "[184px]",
        })}
      >
        {label}
      </span>
    </div>
  );
}

function TierConnectorLine() {
  return (
    <div
      className={css({
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        w: "[137px]",
        h: "[19px]",
        alignSelf: "center",
        mt: "-48",
      })}
    >
      <Image
        src="/images/points/tier-connector-line.svg"
        alt=""
        width={137}
        height={19}
        className={css({ transform: "scaleY(-1) rotate(180deg)" })}
      />
    </div>
  );
}

interface TierRequirementProps {
  pointsRequired: string;
  t: ReturnType<typeof useTranslations<"points.tier">>;
}

function TierRequirement({ pointsRequired, t }: TierRequirementProps) {
  return (
    <div className={stack({ gap: "0", alignItems: "center" })}>
      <div className={flex({ gap: "4", alignItems: "baseline" })}>
        <span className={css({ textStyle: "heading.sm", color: "text.primary" })}>
          {pointsRequired}
        </span>
        <span className={css({ textStyle: "heading.sm", color: "text.tertiary" })}>{t("pts")}</span>
      </div>
      <span
        className={css({
          textStyle: "heading.3xs",
          color: "text.tertiary",
          textTransform: "uppercase",
        })}
      >
        {t("required")}
      </span>
    </div>
  );
}
